import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { 
  Users, 
  Plus, 
  Search, 
  MessageCircle, 
  UserPlus,
  Lock,
  Globe,
  Loader2,
  Send,
  X,
  ArrowLeft
} from 'lucide-react';
import { 
  FadeIn, 
  AnimatedCard,
  StaggerContainer,
  StaggerItem 
} from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

interface Group {
  id: number;
  name: string;
  description: string;
  classId: number;
  subjectId: number;
  createdBy: number;
  maxMembers: number;
  memberCount: number;
  createdAt: string;
}

export function Groups() {
  const { user, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [createData, setCreateData] = useState({
    name: '',
    description: '',
    classId: 9,
    subjectId: 25,
    maxMembers: 50,
  });

  // Fetch groups
  const { data: groups, isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await api.get('/api/groups');
      return res.data as Group[];
    },
  });

  // Fetch group details
  const { data: groupDetail } = useQuery({
    queryKey: ['group', selectedGroup?.id],
    queryFn: async () => {
      if (!selectedGroup) return null;
      const res = await api.get(`/api/groups/${selectedGroup.id}`);
      return res.data;
    },
    enabled: !!selectedGroup,
  });

  // Join group mutation
  const joinMutation = useMutation({
    mutationFn: (groupId: number) => api.post(`/api/groups/${groupId}/join`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Joined group!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to join group');
    },
  });

  // Create group mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/groups', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Group created!');
      setShowCreateModal(false);
      setCreateData({ name: '', description: '', classId: 9, subjectId: 25, maxMembers: 50 });
    },
    onError: () => toast.error('Failed to create group'),
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ groupId, content }: { groupId: number; content: string }) => 
      api.post(`/api/groups/${groupId}/messages`, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', selectedGroup?.id] });
      setNewMessage('');
    },
  });

  const allGroups = groups || [];

  const filteredGroups = allGroups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleJoin = (groupId: number) => {
    if (!isAuthenticated) {
      toast.error('Please login to join');
      return;
    }
    joinMutation.mutate(groupId);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to create a group');
      return;
    }
    createMutation.mutate(createData);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedGroup) return;
    sendMessageMutation.mutate({ groupId: selectedGroup.id, content: newMessage });
  };

  // Group Chat View
  if (selectedGroup) {
    return (
      <div className="space-y-4 h-[calc(100vh-8rem)] flex flex-col">
        {/* Chat Header */}
        <FadeIn>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSelectedGroup(null)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{selectedGroup.name}</h1>
              <p className="text-sm text-slate-500">{groupDetail?.members?.length || 0} members</p>
            </div>
          </div>
        </FadeIn>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 bg-slate-50 rounded-2xl p-4">
          {groupDetail?.messages?.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No messages yet. Start the conversation!</p>
            </div>
          )}
          {groupDetail?.messages?.map((msg: any) => (
            <div 
              key={msg.id} 
              className={`flex gap-3 ${msg.senderId === user?.id ? 'flex-row-reverse' : ''}`}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                {msg.sender?.name?.charAt(0) || '?'}
              </div>
              <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                msg.senderId === user?.id 
                  ? 'bg-primary-600 text-white rounded-tr-sm' 
                  : 'bg-white text-slate-700 rounded-tl-sm shadow-sm'
              }`}>
                <div className="text-xs opacity-70 mb-1">{msg.sender?.name}</div>
                <div className="text-sm">{msg.content}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Message Input */}
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sendMessageMutation.isPending}
            className="px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Study Groups</h1>
            <p className="text-slate-500 mt-1">Join groups and learn together</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Group
          </motion.button>
        </div>
      </FadeIn>

      {/* Search */}
      <FadeIn delay={0.1}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
          />
        </div>
      </FadeIn>

      {/* Groups Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group) => (
            <StaggerItem key={group.id}>
              <AnimatedCard className="p-6 h-full flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6" />
                  </div>
                  <span className="flex items-center gap-1 px-2 py-1 bg-secondary-100 text-secondary-600 text-xs font-medium rounded-full">
                    <Globe className="w-3 h-3" />
                    Public
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-2">{group.name}</h3>
                <p className="text-slate-500 text-sm mb-4 flex-1">{group.description}</p>

                <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {group.memberCount}/{group.maxMembers} members
                  </span>
                </div>

                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleJoin(group.id)}
                    disabled={joinMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                    Join
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedGroup(group)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </motion.button>
                </div>
              </AnimatedCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      )}

      {filteredGroups.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600">No groups found</h3>
          <p className="text-slate-400">Try adjusting your search or create a new group</p>
        </div>
      )}

      {/* Create Group Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-slate-900 mb-4">Create Study Group</h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Group Name *</label>
                  <input
                    type="text"
                    value={createData.name}
                    onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                    placeholder="Enter group name"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={createData.description}
                    onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                    placeholder="What's this group about?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Class ID</label>
                    <input
                      type="number"
                      value={createData.classId}
                      onChange={(e) => setCreateData({ ...createData, classId: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Members</label>
                    <input
                      type="number"
                      value={createData.maxMembers}
                      onChange={(e) => setCreateData({ ...createData, maxMembers: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="flex-1 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
